import store from '@/store';
import { log as debugLog } from '@/utils/debugLogger';

/**
 * Default 3D viewer background.
 *
 * Replaces the former hardcoded dark teal that lived inline in labels3dExt.js:
 *   viewer.setBackgroundColor(14, 42, 42, 4, 22, 22)
 *
 * Single source of truth — never repeat this literal at a call site.
 */
export const VIEWER_BACKGROUND_DEFAULT = '#bfbfbf';

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * Per-user, per-asset localStorage key.
 *
 * Uses the REAL authenticated user id, deliberately NOT store.state.userId.
 * That field is seeded from a URL query parameter (store/index.js:165) and its
 * only setter is commented out (MainView.vue:335), so it is empty in normal use —
 * every account on one machine would collapse onto a single shared key.
 * DisplaySettings.fpsLocalStorageKey (forgeModels.js:4025) has that bug; this
 * deliberately diverges from it rather than inheriting it.
 *
 * This is the same getter fetchUserSettings uses (mongoUtils.js:556), and both
 * call sites of this key run after the user is loaded, so the 'anon' fallback is
 * a safety net rather than an expected value.
 *
 * @returns {string}
 */
export function viewerBackgroundStorageKey() {
  const userId = store.getters['ACCOUNT/GET_USER']?.id ?? 'anon';
  return `BG3D_${userId}_${store.state.assetId}`;
}

/**
 * Module-private on purpose — see the Interfaces note for this task.
 *
 * @param {unknown} hex
 * @returns {boolean} true only for a full '#RRGGBB' string
 */
function isValidViewerBackground(hex) {
  return HEX_COLOR_PATTERN.test(hex);
}

/**
 * Reads the stored colour, falling back to the default.
 *
 * Validation is load-bearing: localStorage is user-writable, and a malformed
 * value would reach parseInt as NaN and paint the canvas black.
 *
 * @returns {string} a valid '#RRGGBB' string
 */
export function readStoredViewerBackground() {
  let stored = null;
  try {
    stored = window.localStorage.getItem(viewerBackgroundStorageKey());
  } catch (err) {
    // localStorage access throws in private-browsing and blocked-storage modes.
    // fetchUserSettings calls this, so an unguarded throw here would take down
    // the whole settings load over a background colour.
    // `err?.message` because a thrown null/undefined would otherwise make this
    // very handler throw — defeating the purpose stated two lines above.
    debugLog('viewerBackground', `localStorage unavailable: ${err?.message}`);
  }
  // Lowercase on the way out. <input type="color"> only ever emits lowercase, so
  // normalising at this boundary is what lets all three consuming tasks compare
  // against VIEWER_BACKGROUND_DEFAULT directly instead of each re-normalising.
  return isValidViewerBackground(stored) ? stored.toLowerCase() : VIEWER_BACKGROUND_DEFAULT;
}

/**
 * Persists the colour. Rejects malformed input rather than storing garbage.
 *
 * @param {string} hex
 * @returns {boolean} false when the value was rejected or could not be persisted
 */
export function storeViewerBackground(hex) {
  if (!isValidViewerBackground(hex)) {
    debugLog('viewerBackground', `Rejected invalid colour: ${hex}`);
    return false;
  }
  const normalised = hex.toLowerCase();
  try {
    window.localStorage.setItem(viewerBackgroundStorageKey(), normalised);
  } catch (err) {
    debugLog('viewerBackground', `Could not persist colour: ${err?.message}`);
    return false;
  }
  debugLog('viewerBackground', `Stored colour ${normalised}`);
  return true;
}

/**
 * Paints a Forge viewer background flat.
 *
 * setBackgroundColor takes two gradient stops; passing identical stops yields a
 * flat colour rather than a gradient.
 *
 * Safe to call before a viewer exists — this runs from GEOMETRY_LOADED_EVENT and
 * from fetchUserSettings, and either can fire first.
 *
 * @param {object} viewer Forge viewer instance
 * @param {string} hex
 */
export function applyViewerBackground(viewer, hex) {
  if (!viewer || typeof viewer.setBackgroundColor !== 'function') {
    // Message covers both arms of the guard — a missing viewer AND a viewer that
    // lacks the method. Naming only the first would mislead whoever debugs the second.
    debugLog('viewerBackground', 'Skipped apply — no usable viewer (absent, or no setBackgroundColor)');
    return;
  }

  const safe = isValidViewerBackground(hex) ? hex.toLowerCase() : VIEWER_BACKGROUND_DEFAULT;
  const r = parseInt(safe.slice(1, 3), 16);
  const g = parseInt(safe.slice(3, 5), 16);
  const b = parseInt(safe.slice(5, 7), 16);

  viewer.setBackgroundColor(r, g, b, r, g, b);
  debugLog('viewerBackground', `Applied ${safe} as rgb(${r},${g},${b})`);
}
